import { ChatBlockMounts } from "./block-mounts";
import { contentBlockAt } from "./content-blocks";
import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import {
  ChatPluginManager,
  CodeBlockPlugin,
} from "./chat-block-plugin";
import { ChatMessage } from "./chat-types";

@component({ name: "std-chat-bubble" })
export class ChatBubble extends LonaWebComponent {
  private $content: HTMLElement = this.$("content");
  private $wrapper: HTMLElement = this.$("wrapper");
  private $caption: HTMLElement = this.$("caption");
  private $debugIndex: HTMLElement = this.$("debug-index");

  private mounts = new ChatBlockMounts();
  private isAssistant: boolean = false;
  private currentIndex: Option<number>;
  private currentMessageId: Option<string>;
  private resizeObserver: Option<ResizeObserver>;
  private lastHeight: number = 0;

  onHeightMeasured: Option<(index: number, height: number) => void>;
  /**
   * Fires when the user clicks an *assistant* bubble. Set by `ChatThread`
   * on pool creation; used by hosts (e.g. ChatSidebar debug column) to
   * focus the clicked turn's debug entry.
   */
  onMessageClick: Option<(messageId: string) => void>;

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(([entry]) => {
      const height = entry.contentRect.height || this.$wrapper.offsetHeight;
      if (
        height > 0 &&
        height !== this.lastHeight &&
        this.currentIndex != null
      ) {
        this.lastHeight = height;
        this.assignStyles({ height: height + "px" });
        this.onHeightMeasured?.(this.currentIndex, height);
      }
    });
    this.resizeObserver.observe(this.$wrapper);

    GESTURE_MANAGER.addPointerEvent(this.$content, {
      onClick: () => {
        if (this.isAssistant && this.currentMessageId != null) {
          this.onMessageClick?.(this.currentMessageId);
        }
      },
      // A transcript is a content surface: the click is an observer, and
      // swallowing pointer-down here made every message unselectable.
      allowTextSelection: true,
    });

    // For new elements: bind() runs in fastdom READ phase before appendChild
    // (WRITE phase), so offsetHeight was 0. Now that we're connected, measure.
    if (this.currentIndex != null && this.lastHeight === 0) {
      const height = this.$wrapper.offsetHeight;
      if (height > 0) {
        this.lastHeight = height;
        this.assignStyles({ height: height + "px" });
        this.onHeightMeasured?.(this.currentIndex, height);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  bind(
    message: ChatMessage,
    pluginManager: ChatPluginManager,
    index: number,
  ): void {
    this.currentIndex = index;
    this.currentMessageId = message.id;

    const blocks = Array.from(message.content, contentBlockAt);
    const isUser = message.role === "user";
    this.isAssistant = !isUser;
    this.$wrapper.style.cursor = this.isAssistant ? "pointer" : "default";
    this.$wrapper.toggleAttribute("user", isUser);
    this.$wrapper.toggleAttribute("assistant", !isUser);
    // The role rides on the HOST too: turn elements live in the thread's
    // shadow tree, so a theme sheet can target std-chat-bubble[user] and
    // set the custom-property hooks per role from outside.
    this.toggleAttribute("user", isUser);
    this.toggleAttribute("assistant", !isUser);
    // So does CODE-ness: a message whose whole content is code (the
    // adapter classified it) marks the host, and a look can restyle the
    // bubble itself — ground, width, padding — without piercing the shadow.
    this.toggleAttribute(
      "code",
      blocks.some((block) => block.type === "code") &&
        blocks.every((block) => block.type === "code" || block.type === "quiet_gap"),
    );

    this.mounts.clear();
    DomUtils.clearChildren(this.$content);

    for (const [blockIndex, block] of blocks.entries()) {
      const pluginType =
        this.isAssistant && block.type === "text" ? "markdown" : block.type;
      const plugin = pluginManager.resolve(block.type, pluginType);
      const $block = this.mounts.mount(plugin, block, message.isStreaming, {
        key: JSON.stringify([message.id, block.id ?? blockIndex]), disclosure: pluginManager.disclosure,
      });
      // The mount retains its provider index, but an intentional gap owns
      // neither bubble chrome nor a flex item (and therefore no block gap).
      if (block.type === "quiet_gap") continue;
      // Each content block is its OWN bubble — a turn with several blocks
      // draws as a grouped run, the way iMessage draws consecutive
      // messages. The chrome lives on this wrapper; deltas still target
      // the block element inside it. Blocks that carry their OWN chrome
      // (a tool-call card) render bare — a bubble around them would be
      // chrome on chrome, the way iMessage draws an app card outside the
      // gray bubble.
      const $bubble = document.createElement("div");
      $bubble.className = "bubble";
      $bubble.toggleAttribute("bare", block.type === "tool_call");
      $bubble.appendChild($block);
      this.$content.appendChild($bubble);
    }

    if (this.isAssistant && message.streamStartedAt && message.streamEndedAt) {
      const elapsed = message.streamEndedAt.durationSince(
        message.streamStartedAt,
      );
      this.$caption.textContent = elapsed.formatLatency();
      this.$caption.style.display = "block";
    } else {
      this.$caption.textContent = "";
      this.$caption.style.display = "none";
    }

    this.$debugIndex.textContent = String(index);

    // Eager sync measurement — works for recycled elements (already in DOM)
    const height = this.$wrapper.offsetHeight;
    if (height > 0) {
      this.lastHeight = height;
      this.assignStyles({ height: height + "px" });
      this.onHeightMeasured?.(this.currentIndex, height);
    }
  }

  appendDelta(blockIndex: number, delta: string): void {
    this.mounts.appendDelta(blockIndex, delta);
  }

  finalizeBlock(blockIndex: number): void {
    this.mounts.finalize(blockIndex);
  }

  recycle(): void {
    this.mounts.clear();
    DomUtils.clearChildren(this.$content);
    this.$caption.textContent = "";
    this.$caption.style.display = "none";
    this.$debugIndex.textContent = "";
    this.lastHeight = 0;
    this.currentIndex = null;
    this.currentMessageId = null;
    this.onHeightMeasured = null;
    this.onMessageClick = null;
    this.toggleAttribute("user", false);
    this.toggleAttribute("assistant", false);
    this.toggleAttribute("code", false);
  }

  getMeasuredHeight(): number {
    return this.$wrapper.offsetHeight;
  }

  static $styles: Style.Compat[] = [
    CodeBlockPlugin.$panelStyle,
    css`
      :host {
        display: block;
        width: 100%;
        box-sizing: border-box;
        overflow: hidden;
      }

      #wrapper {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        display: flex;
        flex-direction: column;
      }

      #wrapper[user] {
        align-items: flex-end;
      }

      #wrapper[assistant] {
        align-items: flex-start;
      }

      /* The turn's column: the row's gutter and the role's alignment.
         Chrome lives on the bubbles inside — one per content block.

         The inline inset is the ROW's own: the thread never pads, so each
         turn carries its horizontal gutter through --chat-turn-inset (the
         product's 16px as the default). A centered-measure look states its
         max(gutter, (100% - measure) / 2) here and the percentage resolves
         against the row — the same width the thread frame had. */
      #content {
        display: flex;
        flex-direction: column;
        align-self: stretch;
        margin: 0 var(--chat-turn-inset, 16px);
        gap: var(--chat-bubble-gap, 2px);
      }

      #wrapper[user] #content {
        align-items: flex-end;
      }

      #wrapper[assistant] #content {
        align-items: flex-start;
        padding: var(--chat-bubble-assistant-block-inset, 8px) 0;
        gap: var(--chat-bubble-assistant-gap, 8px);
      }

      /* Every property of the user chrome is a hook with today's product
         value as its default, because a "look" (a chatgpt.com or claude.ai
         replica) is exactly a re-statement of this chrome — ground, ink,
         radius, width, face — from a theme sheet, per role, via the host
         attribute. */
      .bubble {
        max-width: var(--chat-bubble-max-width, 80%);
        padding: var(--chat-bubble-padding, 10px 14px);
        border-radius: var(--chat-bubble-radius, 12px);
        line-height: var(--chat-bubble-leading, 1.4);
        box-sizing: border-box;
      }

      #wrapper[user] .bubble {
        background: var(--chat-bubble-ground, var(--bg-btn-pri));
        color: var(--chat-bubble-ink, #fff);
        border: var(--chat-bubble-edge, none);
        font-family: var(--chat-bubble-face, inherit);
      }

      /* The grouped run, iMessage's own grammar: within a turn, the
         corners where adjacent bubbles face each other stand down to a
         small radius — BOTH corners of a facing edge, because a run's
         wide bubbles (a command panel) meet across their whole width and
         a one-sided shrink reads lopsided — and the tail corner survives
         on the run's last bubble. */
      #wrapper[user] .bubble:last-child {
        border-bottom-right-radius: var(
          --chat-bubble-tail-radius,
          var(--chat-bubble-radius, 4px)
        );
      }

      #wrapper[user] .bubble:not(:last-child) {
        border-bottom-right-radius: var(--chat-bubble-adjacent-radius, 4px);
        border-bottom-left-radius: var(--chat-bubble-adjacent-radius, 4px);
      }

      #wrapper[user] .bubble:not(:first-child) {
        border-top-right-radius: var(--chat-bubble-adjacent-radius, 4px);
        border-top-left-radius: var(--chat-bubble-adjacent-radius, 4px);
      }

      /* The assistant side ships bare — the product's document style — but
         every property is a hook too, so a look that grounds the received
         side (a true iMessage replica's gray) states it per role. The
         grouped-corner rules mirror the user side on the LEFT edge, with
         0 defaults so the bare document never shows them. */
      #wrapper[assistant] .bubble {
        max-width: var(--chat-bubble-assistant-max-width, 100%);
        background: var(--chat-bubble-assistant-ground, none);
        border-radius: var(--chat-bubble-assistant-radius, 0);
        padding: var(--chat-bubble-assistant-padding, 0);
        color: var(--chat-bubble-assistant-ink, var(--text-pri, #000));
      }

      #wrapper[assistant] .bubble:last-child {
        border-bottom-left-radius: var(
          --chat-bubble-assistant-tail-radius,
          var(--chat-bubble-assistant-radius, 0)
        );
      }

      #wrapper[assistant] .bubble:not(:last-child) {
        border-bottom-left-radius: var(--chat-bubble-adjacent-radius, 0px);
        border-bottom-right-radius: var(--chat-bubble-adjacent-radius, 0px);
      }

      #wrapper[assistant] .bubble:not(:first-child) {
        border-top-left-radius: var(--chat-bubble-adjacent-radius, 0px);
        border-top-right-radius: var(--chat-bubble-adjacent-radius, 0px);
      }

      /* Last, so it wins the order tie against every chrome rule above:
         a bare wrapper contributes nothing — the block inside is the
         whole presentation. */
      #wrapper[user] .bubble[bare],
      #wrapper[assistant] .bubble[bare] {
        background: none;
        border: none;
        padding: 0;
        border-radius: 0;
      }

      #caption {
        display: none;
        color: var(--text-ter, #999);
        margin: 0px var(--chat-turn-inset, 16px) 0;
        align-self: flex-start;
        margin-bottom: 12px;
      }

      :host([debug-boundaries]) {
        outline: 1px solid var(--red, red);
        outline-offset: -1px;
      }

      #debug-index {
        display: none;
      }

      :host([debug-boundaries]) #debug-index {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 20px;
        font-size: 9px;
        font-family: monospace;
        color: var(--red, red);
        background: color-mix(in srgb, var(--red, red) 8%, transparent);
        border-right: 1px solid var(--red, red);
        z-index: 1;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="wrapper">
      <std-col id="content"></std-col>
      <p id="caption" class=small></p>
    </div>
    <div id="debug-index"></div>
  `;
}

export namespace ChatBubble {
  export type Props = {
    message: ChatMessage;
    pluginManager: ChatPluginManager;
    index: number;
  };
}
